import { useCallback, useEffect, useMemo, useState } from 'react';
import { Menu } from 'antd';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { CompanyForm } from './TabForm/CompanyForm';
import { PeopleForm } from './TabForm/PeopleForm';
import { BrmForm } from './TabForm/BrmForm';
import { primaryColor, greyColor, appBgColor, primaryFont, wMedium, maxTablet, maxMobile } from '../../../Utils/utilsStyle';
import { DropdownFooter } from '../../common/dropdown/DropdownFooter';
import { actions as searchActions } from '../../../Slice/AdvanceSearchSlice';
import { actions as uiActions } from '../../../Slice/UISlice';
import { actions as flurryActions } from '../../../Slice/HelperSlice';
import { valueToObject } from '../../../Utils/UtilityFunctions';
import {
  selectAdvanceSearchPeopleForm,
  selectAdvanceSearchCompanyForm,
  selectAdvanceSearchBrmForm,
  selectAdvanceSearchMetadataLoading,
  selectAdvanceSearchMetadata,
  selectAdvanceSearchActiveTab,
} from '../../../Selector/AdvanceSearchSelector';
import { selectAdvanceSearchOpen } from '../../../Selector/UISelector';
import {
  AdvanceSearchRequest,
  PeopleFormData,
  CompanyFormData,
  BuyerGroupFormData
} from '../../../Types/AdvanceSearchResultsTypes';
import { ADVANCE_SEARCH_ROUTE } from '../../../Utils/Constants';

const Wrapper = styled.div`
.ant-dropdown-menu {
  padding: 8px 16px;
  background-color: ${appBgColor};
  border-bottom: 1px solid #DADADA;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  box-shadow: none;
  gap: 12px;
  .ant-dropdown-menu-item:hover {
    background-color: transparent;
  }
  .ant-dropdown-menu-item-selected {
    background-color: transparent;
  }
  @media all and (max-width: ${maxMobile}) {
    gap: 4px;
  }
}
.tab-link {
  padding: 0px 16px;
  border-radius: 8px;
  background: #FFF;
  border: 1px solid #DADADA;
  color: ${greyColor} !important;
  font-family: ${primaryFont};
  font-size: 14px;
  font-weight: ${wMedium};
  letter-spacing: -0.4px;
  height: 28px;
  cursor: pointer;
  &:active, &:hover {
    color: ${primaryColor} !important;
    background: #DEF6E6;
  }
  &.active {
    color: ${primaryColor} !important;
    background: #DEF6E6;
    border-color: ${primaryColor};
  }
  @media all and (max-width: ${maxTablet}) {
    font-size: 12px;
    padding: 0px 12px;
  }
  @media all and (max-width: ${maxMobile}) {
    font-size: 11px;
  }
  .ant-divider {
    margin: 0;
  }
}
.clear-btn {
  margin-left: auto;
  background: none;
  border: none;
  padding: 0;
  color: ${primaryColor};
  font-family: ${primaryFont};
  font-size: 14px;
  font-weight: ${wMedium};
  letter-spacing: -0.4px;
  line-height: normal;
  cursor: pointer;
  &:hover {
    opacity: 0.8;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  @media all and (max-width: ${maxTablet}) {
    font-size: 12px;
  }
  @media all and (max-width: ${maxMobile}) {
    font-size: 11px;
  }
}
`;

const TabPanel = styled.div<{ $visible: boolean }>`
  display: ${({ $visible }) => ($visible ? 'block' : 'none')};
`;

type AnyFormData = PeopleFormData | CompanyFormData | BuyerGroupFormData;

const advanceSearchTabs = [
  { label: 'People', value: 'people' },
  { label: 'Company', value: 'company' },
  { label: 'Buyer Group', value: 'brm' },
];

const isFormFilled = (data: AnyFormData | null) => {
  if (!data) return false;
  return Object.values(data).some((val) => {
    if (typeof val === 'number') {
      return val !== 0;
    }
    return String(val).trim() !== '';
  });
};

const buildRequest = (
  tabIndex: number,
  formData: AnyFormData,
): AdvanceSearchRequest => {
  if (tabIndex === 0) {
    const d = formData as PeopleFormData;
    return {
      searchType: 'person',
      fullName: d.fullName || '',
      designation: d.designation || '',
      currentCompany: d.currentCo || '',
      pastCompany: d.previousCo || '',
      country: d.country || '',
      region: d.region || '',
    };
  }
  if (tabIndex === 1) {
    const d = formData as CompanyFormData;
    return {
      searchType: 'company',
      name: d.name || '',
      industry: d.industry || '',
      revenue: d.revenue || '',
      size: d.size || '',
      country: d.country || '',
      region: d.region || '',
    };
  }

  const d = formData as BuyerGroupFormData;
  return {
    searchType: 'brm',
    name: d.name || '',
    member: d.member ? String(d.member) : '',
    size: d.size || '',
    sharedBy: d.sharedBy ? String(d.sharedBy) : '',
  };
};

const AdvanceSearchMenu = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const peopleForm = useSelector(selectAdvanceSearchPeopleForm);
  const companyForm = useSelector(selectAdvanceSearchCompanyForm);
  const brmForm = useSelector(selectAdvanceSearchBrmForm);
  const isLoading = useSelector(selectAdvanceSearchMetadataLoading);
  const advanceSearchMetadata = useSelector(selectAdvanceSearchMetadata);
  const searchActiveTab = useSelector(selectAdvanceSearchActiveTab);
  const isMenuOpen = useSelector(selectAdvanceSearchOpen);

  const [activeTab, setActiveTab] = useState(searchActiveTab);
  const [mountedTabs, setMountedTabs] = useState<Set<number>>(() => new Set([searchActiveTab]));

  const host = window.location.href;

  useEffect(() => {
    if (isMenuOpen) {
      setActiveTab(searchActiveTab);
      setMountedTabs((prev) => new Set([...prev, searchActiveTab]));
    }
  }, [isMenuOpen, searchActiveTab]);

  const handleTabClick = useCallback((index: number) => {
    setActiveTab(index);
    setMountedTabs((prev) => new Set([...prev, index]));
    dispatch(flurryActions.callFlurryEvent(
      valueToObject(`tab_selected_${advanceSearchTabs[index]?.value ?? ''}`, 0, {
        host,
      }),
    ));
  }, [dispatch, host]);

  const activeFormData = useMemo(
    () => (activeTab === 0 ? peopleForm : activeTab === 1 ? companyForm : brmForm),
    [activeTab, brmForm, companyForm, peopleForm],
  );

  const isFormValid = useMemo(() => isFormFilled(activeFormData), [activeFormData]);

  const handleClearAll = useCallback(() => {
    dispatch(searchActions.clearAllForms());
  }, [dispatch]);

  const handleApply = useCallback(() => {
    if (!isFormValid) return;
    const request = buildRequest(activeTab, activeFormData);
    const filledFields = Object.fromEntries(
      Object.entries(request).filter(([key, value]) => key !== 'searchType' && String(value).trim() !== ''),
    );
    dispatch(flurryActions.callFlurryEvent(
      valueToObject(`search_executed_${advanceSearchTabs[activeTab]?.value ?? ''}`, 0, {
        search_query: JSON.stringify(filledFields),
        host,
      }),
    ));
    dispatch(searchActions.triggerAdvanceSearch(request));
    dispatch(uiActions.isAdvanceSearchOpen(false));
    dispatch(uiActions.isSearchOpen(false));
    const params = new URLSearchParams();
    Object.entries(request).forEach(([key, value]) => {
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        params.set(key, String(value));
      }
    });
    navigate(`${ADVANCE_SEARCH_ROUTE}?${params.toString()}`);
  }, [activeFormData, activeTab, dispatch, host, isFormValid, navigate]);

  const brmSizes = advanceSearchMetadata?.brmSizes ?? [];

  return (
    <Wrapper className='advance-search-menu-wrapper' onClick={(e) => e.stopPropagation()}>
      <Menu selectedKeys={[String(activeTab)]} className='d-flex'>
        {advanceSearchTabs.map((tab, index) => (
          <div
            key={tab.value}
            className={`tab-link d-flex align-items-center ${activeTab === index ? 'active' : ''}`}
            onClick={() => handleTabClick(index)}
          >
            {tab.label}
          </div>
        ))}
        <button type='button' disabled={!isFormValid} className='clear-btn' onClick={handleClearAll}>Clear All</button>
      </Menu>
      <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
        {mountedTabs.has(0) && (
          <TabPanel $visible={activeTab === 0}>
            <PeopleForm isLoading={isLoading} />
          </TabPanel>
        )}
        {mountedTabs.has(1) && (
          <TabPanel $visible={activeTab === 1}>
            <CompanyForm isLoading={isLoading} />
          </TabPanel>
        )}
        {mountedTabs.has(2) && (
          <TabPanel $visible={activeTab === 2}>
            <BrmForm isLoading={isLoading} brmSizes={brmSizes} />
          </TabPanel>
        )}
        <DropdownFooter title='Apply' disabled={!isFormValid} onApply={handleApply} isInfoSection={true} infoMsg="Please fill in at least one field to apply filters." />
      </form>
    </Wrapper>
  );
};

export default AdvanceSearchMenu;
